import { type ReactElement, Suspense, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import ReactFlow, { Background, type Edge, type Node, ReactFlowProvider } from "reactflow"

import { Tag, Typography } from "@synapse-analytics/synapse-ui"
import moment from "moment"

import { LoadingContainer } from "../../../../components/core/LoadingContainer"
import { MonacoTextEditor } from "../../../../components/data-display/MonacoTextEditor"
import { edgeTypes, nodeTypes } from "../../../Predictions/Interfaces"
import type { WorkFlowPathProps } from "../../interfaces"
import { getWorkflowStatusTagConfig, toNodeType } from "../../utils/helpers"
import { GraphJsonSwitch } from "../Elements/GraphJsonSwitch/GraphJsonSwitch"

import styles from "./WorkflowPath.module.scss"

/**
 * Vertical flow rendering for the satisfied workflow path of a prediction.
 */
export const WorkFlowPath = (props: WorkFlowPathProps): ReactElement => {
  const { satisfiedFlowPath, workflowDetails } = props
  const { t } = useTranslation("decisions", { keyPrefix: "workflowPath" })
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [isJsonMode, setIsJsonMode] = useState<boolean>(false)

  const { nodes, edges } = useMemo(() => {
    const NODE_HEIGHT = 50
    const VERTICAL_SPACING = 40
    const START_X = 100
    const START_Y = 50

    const builtNodes: Node[] = satisfiedFlowPath.map((item, idx) => ({
      id: item.uuid,
      type: toNodeType(item.type),
      data: {
        label: item.title,
        nodeType: toNodeType(item.type),
        isSelected: selectedId === item.uuid,
        nodeName: item.name,
        scorecard_set: { name: item.name },
        rulesetName: item.name,
        scriptName: item.name,
        program: { name: item.name },
        taglist: { name: item.name },
        project: { name: item.name },
        return_label: { name: item.name },
      },
      position: {
        x: START_X,
        y: START_Y + idx * (NODE_HEIGHT + VERTICAL_SPACING),
      },
    }))

    const builtEdges: Edge[] = satisfiedFlowPath.slice(1).map((_, idx) => ({
      id: `edge-${idx}`,
      source: satisfiedFlowPath[idx]?.uuid ?? "",
      target: satisfiedFlowPath[idx + 1]?.uuid ?? "",
      type: "smoothstep",
      animated: true,
    }))

    return { nodes: builtNodes, edges: builtEdges }
  }, [satisfiedFlowPath, selectedId])

  const handleNodeClick = (_: React.MouseEvent, node: Node): void =>
    setSelectedId(node.id === selectedId ? null : node.id)

  const statusTagConfig = getWorkflowStatusTagConfig(workflowDetails)

  return (
    <div className={styles.workflowContainer}>
      <div className={styles.workflowHeader}>
        <div className={styles.workflowHeaderDetails}>
          <Typography variant="p" className={styles.workflowTitle}>
            {workflowDetails?.name}
          </Typography>
          <div className={styles.tags}>
            <Tag color="blue" size="small">
              v{workflowDetails?.version}
            </Tag>

            {statusTagConfig && (
              <Tag color={statusTagConfig.color} size="small">
                {t(statusTagConfig.translationKey)}
              </Tag>
            )}
          </div>
        </div>
        <GraphJsonSwitch isJsonMode={isJsonMode} handleSwitchMode={() => setIsJsonMode(!isJsonMode)} />
      </div>

      <div className={`${styles.workflowBody}`}>
        {isJsonMode ? (
          <MonacoTextEditor
            value={JSON.stringify((satisfiedFlowPath as {}) ?? {}, null, 2)}
            language="json"
            height="100%"
            readOnly
            enableCopy
          />
        ) : (
          <ReactFlowProvider>
            <Suspense fallback={<LoadingContainer />}>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                edgeTypes={edgeTypes}
                nodeTypes={nodeTypes}
                nodesDraggable={false}
                nodesConnectable={false}
                onNodeClick={handleNodeClick}
                fitView
                fitViewOptions={{ padding: 0.2 }}
              >
                <Background />
              </ReactFlow>
            </Suspense>
          </ReactFlowProvider>
        )}
      </div>

      <div className={styles.workflowFooter}>
        <Typography variant="p" className={styles.footerText}>
          <strong>{t("footer.createdBy")}</strong>
          {workflowDetails?.created_by}
        </Typography>
        <Typography variant="p" className={styles.footerText}>
          <strong>{t("footer.createdAt")}</strong>
          {moment(workflowDetails?.created_at as string)
            .utc()
            .format("MMM DD YYYY, H:mm:ss")}
        </Typography>
      </div>
    </div>
  )
}
